/**
 * メタタグ管理ユーティリティ
 * React 19対応（react-helmet-asyncの代替）
 */

/**
 * サイトのベースURLを取得
 */
export const getSiteUrl = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  return `${window.location.protocol}//${window.location.host}`;
};

/**
 * メタタグの値をサニタイズ
 * X（旧Twitter）のOGP検証ツールは、空文字列や無効な値に対してエラーを出すため
 */
const sanitizeMetaContent = (content: string | undefined | null): string | null => {
  if (!content || typeof content !== 'string') {
    return null;
  }
  
  // 前後の空白を削除
  let sanitized = content.trim();
  
  // 空文字列の場合はnullを返す
  if (sanitized === '') {
    return null;
  }
  
  // HTMLタグを削除（セキュリティ対策、最初に実行）
  sanitized = sanitized.replace(/<[^>]*>/g, '');
  
  // 制御文字（0x00-0x1F、0x7F-0x9F）を削除
  // ただし、改行（\r\n）やタブ（\t）は後で処理するため、ここでは削除しない
  sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F-\x9F]/g, '');
  
  // 改行、タブ、連続する空白を削除
  sanitized = sanitized.replace(/[\r\n\t]+/g, ' ').replace(/\s+/g, ' ').trim();
  
  // 最終的な空文字列チェック
  if (sanitized === '') {
    return null;
  }
  
  return sanitized;
};

/**
 * タイトルをOGP/Twitter Card用に制限
 * 公式ドキュメントの推奨値:
 * - Twitter Card: 34文字以内（PC表示の推奨値）
 * - OGP: 50文字以内（PC表示の推奨値）
 * 
 * 注意: タイトルは先にサニタイズ処理が行われるため、ここでは文字数制限のみを行う
 */
const truncateTitle = (title: string, maxLength: number): string => {
  if (!title || typeof title !== 'string') {
    return '';
  }
  
  // 文字数が制限以内の場合はそのまま返す
  if (title.length <= maxLength) {
    return title;
  }
  
  // 最大長を超える場合は、最後の3文字を「...」に置き換え
  // ただし、maxLengthが3未満の場合は「...」を付けない
  if (maxLength <= 3) {
    return title.substring(0, maxLength);
  }
  
  return title.substring(0, maxLength - 3) + '...';
};

/**
 * メタタグを更新または追加
 */
export const setMetaTag = (name: string, content: string, attribute: 'name' | 'property' = 'name'): void => {
  if (typeof document === 'undefined') return;

  // コンテンツをサニタイズ
  const sanitizedContent = sanitizeMetaContent(content);
  
  // 無効な値の場合はメタタグを削除して終了
  if (sanitizedContent === null) {
    removeMetaTag(name, attribute);
    return;
  }

  const selector = attribute === 'name' 
    ? `meta[name="${name}"]` 
    : `meta[property="${name}"]`;
  
  let element = document.querySelector(selector) as HTMLMetaElement;
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', sanitizedContent);
};

/**
 * メタタグを削除
 */
export const removeMetaTag = (name: string, attribute: 'name' | 'property' = 'name'): void => {
  if (typeof document === 'undefined') return;

  const selector = attribute === 'name' 
    ? `meta[name="${name}"]` 
    : `meta[property="${name}"]`;
  
  const element = document.querySelector(selector);
  if (element) {
    element.remove();
  }
};

/**
 * タイトルを設定
 */
export const setTitle = (title: string): void => {
  if (typeof document === 'undefined') return;
  document.title = title;
};

/**
 * Canonical URLを設定
 */
export const setCanonicalUrl = (url: string): void => {
  if (typeof document === 'undefined') return;

  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  
  link.setAttribute('href', url);
};

/**
 * OGPタグを設定
 */
export interface OGPMetaTags {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
}

export const setOGPTags = (tags: OGPMetaTags): void => {
  const siteUrl = getSiteUrl();
  
  // タイトルをサニタイズしてから50文字以内に制限（OGPの推奨値：PC表示で最大50文字）
  // サニタイズ処理を先に行い、その後文字数制限を行う
  const sanitizedTitle = sanitizeMetaContent(tags.title);
  if (sanitizedTitle) {
    const truncatedTitle = truncateTitle(sanitizedTitle, 50);
    if (truncatedTitle) {
      setMetaTag('og:title', truncatedTitle, 'property');
    }
  }
  setMetaTag('og:description', tags.description, 'property');
  setMetaTag('og:type', tags.type || 'article', 'property');
  setMetaTag('og:url', tags.url || siteUrl, 'property');
  
  if (tags.image) {
    // 画像URLが相対パスの場合は絶対URLに変換
    const imageUrl = tags.image.startsWith('http') 
      ? tags.image 
      : `${siteUrl}${tags.image}`;
    setMetaTag('og:image', imageUrl, 'property');
  }
  
  if (tags.siteName) {
    setMetaTag('og:site_name', tags.siteName, 'property');
  }
};

/**
 * Twitter Cardタグを設定
 */
export interface TwitterCardMetaTags {
  card?: 'summary' | 'summary_large_image';
  title: string;
  description: string;
  image?: string;
}

export const setTwitterCardTags = (tags: TwitterCardMetaTags): void => {
  const siteUrl = getSiteUrl();
  
  // タイトルをサニタイズしてから34文字以内に制限（Twitter Cardの推奨値：PC表示で最大34文字）
  // サニタイズ処理を先に行い、その後文字数制限を行う
  const sanitizedTitle = sanitizeMetaContent(tags.title);
  setMetaTag('twitter:card', tags.card || 'summary_large_image', 'name');
  if (sanitizedTitle) {
    const truncatedTitle = truncateTitle(sanitizedTitle, 34);
    if (truncatedTitle) {
      setMetaTag('twitter:title', truncatedTitle, 'name');
    }
  }
  setMetaTag('twitter:description', tags.description, 'name');
  
  if (tags.image) {
    // 画像URLが相対パスの場合は絶対URLに変換
    const imageUrl = tags.image.startsWith('http') 
      ? tags.image 
      : `${siteUrl}${tags.image}`;
    setMetaTag('twitter:image', imageUrl, 'name');
  }
};

/**
 * 基本メタタグを設定
 */
export interface BasicMetaTags {
  title: string;
  description: string;
  keywords?: string;
}

export const setBasicMetaTags = (tags: BasicMetaTags): void => {
  setTitle(tags.title);
  setMetaTag('description', tags.description);
  
  if (tags.keywords) {
    setMetaTag('keywords', tags.keywords);
  }
};

/**
 * すべてのメタタグをクリア（OGP、Twitter Card）
 */
export const clearMetaTags = (): void => {
  // OGPタグを削除
  const ogpProperties = [
    'og:title',
    'og:description',
    'og:image',
    'og:url',
    'og:type',
    'og:site_name',
  ];
  
  ogpProperties.forEach(prop => {
    removeMetaTag(prop, 'property');
  });
  
  // Twitter Cardタグを削除
  const twitterNames = [
    'twitter:card',
    'twitter:title',
    'twitter:description',
    'twitter:image',
  ];
  
  twitterNames.forEach(name => {
    removeMetaTag(name, 'name');
  });
};

